import type { ReactNode } from 'react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@azelis/ui/components/combobox';
import { Field, FieldError, FieldGroup, FieldLabel } from '@azelis/ui/components/field';
import { InputGroupAddon } from '@azelis/ui/components/input-group';
import { LoaderCircle } from 'lucide-react';

import { FileUploadField } from './file-upload-field';
import { useSubmitWizard } from './submit-wizard-context';

export function QuestionnaireStep() {
  const {
    mode,
    productQuery,
    setProductQuery,
    products,
    productsError,
    productsLoading,
    selectProduct,
    countryQuery,
    setCountryQuery,
    matchingCountries,
    countriesError,
    countriesLoading,
    selectCountry,
    questionnaireFile,
    setQuestionnaireFile,
    freeTextQuestions,
    setFreeTextQuestions,
  } = useSubmitWizard();

  const questionsTextareaClassName =
    'h-36 min-h-36 w-full min-w-0 rounded-2xl border border-input bg-background px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

  // The file variant centres a single 320px column; the free-text variant needs
  // the full card width for the questions textarea.
  const fieldGroupClassName =
    mode === 'file' ? 'mx-auto w-80 gap-6 py-8' : 'w-full gap-6 py-8';

  return (
    <FieldGroup className={fieldGroupClassName}>
      <Field className="w-80" data-invalid={productsError || undefined}>
        <FieldLabel htmlFor="product-search">Product</FieldLabel>
        <WizardCombobox
          emptyText={productQuery.trim() ? 'No products found.' : 'Type to search.'}
          id="product-search"
          inputValue={productQuery}
          invalid={productsError}
          itemToStringLabel={(product) => product.name}
          items={products}
          loading={productsLoading}
          placeholder="Search by BP/PIM/PCR/ERP number"
          renderItem={(product) => (
            <>
              {product.matchedCode ?? product.productId}
              {product.matchedCode && (
                <span className="text-muted-foreground"> (also {product.productId})</span>
              )}
              {' — '}
              {product.name}
            </>
          )}
          onInputValueChange={setProductQuery}
          onSelect={selectProduct}
        />
        {productsError ? (
          <FieldError>
            Product search is unavailable right now. Try again later, or contact support if this
            persists.
          </FieldError>
        ) : null}
      </Field>

      <Field className="w-80" data-invalid={countriesError || undefined}>
        <FieldLabel htmlFor="country-search">Country</FieldLabel>
        <WizardCombobox
          emptyText="No countries found."
          id="country-search"
          inputValue={countryQuery}
          invalid={countriesError}
          itemToStringLabel={(country) => country.name}
          items={matchingCountries}
          loading={countriesLoading}
          placeholder="Select a Country"
          renderItem={(country) => country.name}
          showTrigger
          onInputValueChange={setCountryQuery}
          onSelect={selectCountry}
        />
        {countriesError ? (
          <FieldError>
            Country list is unavailable right now. Try again later, or contact support if this
            persists.
          </FieldError>
        ) : null}
      </Field>

      {mode === 'file' ? (
        <Field className="w-80">
          <FieldLabel htmlFor="questionnaire-file">Questionnaire</FieldLabel>
          <FileUploadField
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            files={questionnaireFile ? [questionnaireFile] : []}
            id="questionnaire-file"
            onFilesChange={(files) => {
              setQuestionnaireFile(files[0] ?? null);
            }}
          />
        </Field>
      ) : (
        <Field>
          <FieldLabel htmlFor="free-text-questions">Questions</FieldLabel>
          <textarea
            id="free-text-questions"
            className={questionsTextareaClassName}
            placeholder="One question per line..."
            value={freeTextQuestions}
            onChange={(event) => {
              setFreeTextQuestions(event.target.value);
              setQuestionnaireFile(null);
            }}
          />
        </Field>
      )}
    </FieldGroup>
  );
}

function WizardCombobox<T extends { id: string }>({
  emptyText,
  id,
  inputValue,
  invalid,
  items,
  itemToStringLabel,
  loading,
  onInputValueChange,
  onSelect,
  placeholder,
  renderItem,
  showTrigger = false,
}: {
  emptyText: string;
  id: string;
  inputValue: string;
  invalid?: boolean;
  items: Array<T>;
  itemToStringLabel: (item: T) => string;
  loading?: boolean;
  onInputValueChange: (value: string) => void;
  onSelect: (item: T) => void;
  placeholder: string;
  renderItem: (item: T) => ReactNode;
  showTrigger?: boolean;
}) {
  return (
    <Combobox
      autoComplete="off"
      filter={null}
      inputValue={inputValue}
      items={items}
      itemToStringLabel={itemToStringLabel}
      isItemEqualToValue={(a, b) => a.id === b.id}
      onInputValueChange={onInputValueChange}
      onValueChange={(item) => {
        if (item) {
          onSelect(item);
        }
      }}
    >
      <ComboboxInput
        aria-invalid={invalid || undefined}
        className="w-full"
        id={id}
        placeholder={placeholder}
        showSearch={false}
        showTrigger={showTrigger}
      >
        {loading ? (
          <InputGroupAddon align="inline-end">
            <LoaderCircle aria-hidden className="animate-spin" />
          </InputGroupAddon>
        ) : null}
      </ComboboxInput>
      <ComboboxContent>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => (
            <ComboboxItem key={item.id} value={item}>
              {renderItem(item)}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
